'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaChevronUp } from '@/components/icons';
import { COLORS } from '@/constants/colors';

interface FAQ {
  id: number;
  question: string;
  /** Plain string (existing usage) or JSX/ReactNode when an answer needs an inline link. */
  answer: React.ReactNode;
}

interface FAQSectionProps {
  faqs: FAQ[];
  title?: string;
  /** Bolds the full answer text as a direct-answer block (for AI/featured-snippet extraction). */
  boldAnswer?: boolean;
}

/**
 * Shared FAQ Section Component
 * Used across multiple pages: Home, Shop by Vehicle, Measure Filter, etc.
 *
 * @param faqs - Array of FAQ objects with id, question, and answer
 * @param title - Optional section title (defaults to "Frequently Asked Questions")
 * @param boldAnswer - Bolds the answer text as a direct-answer block
 */
const FAQSection = ({ faqs, title = 'Frequently Asked Questions', boldAnswer = false }: FAQSectionProps) => {
  const [openId, setOpenId] = useState<number | null>(null);

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <Section>
      <Container>
        <HeadingBlock>
          <Headline>{title}</Headline>
          <Accent />
        </HeadingBlock>

        <FAQList>
          {faqs.map((faq) => (
            <FAQItem key={faq.id}>
              <FAQQuestion onClick={() => toggleFAQ(faq.id)} $isOpen={openId === faq.id}>
                <QuestionText>{faq.question}</QuestionText>
                <IconWrapper>
                  {openId === faq.id ? <FaChevronUp /> : <FaChevronDown />}
                </IconWrapper>
              </FAQQuestion>
              <FAQAnswer $isOpen={openId === faq.id}>
                <AnswerText>{boldAnswer ? <strong>{faq.answer}</strong> : faq.answer}</AnswerText>
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQList>
      </Container>
    </Section>
  );
};

export default React.memo(FAQSection);

const Section = styled.section`
  background: #fff;
  padding: 80px 24px;

  @media (max-width: 1024px) {
    padding: 64px 24px;
  }

  @media (max-width: 767px) {
    padding: 48px 16px;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeadingBlock = styled.div`
  max-width: 640px;
  margin: 0 auto 56px;
  text-align: center;

  @media (max-width: 767px) {
    margin-bottom: 40px;
  }
`;

const Headline = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 14px;
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const Accent = styled.div`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: 0 auto;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FAQItem = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${COLORS.brandGreen};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const FAQQuestion = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  background: ${(props) => (props.$isOpen ? '#f8f9fa' : '#fff')};
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;
  text-align: left;

  &:hover {
    background: #f8f9fa;
  }

  @media (max-width: 767px) {
    padding: 20px;
  }
`;

const QuestionText = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  padding-right: 16px;

  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const IconWrapper = styled.div`
  color: ${COLORS.brandGreen};
  font-size: 18px;
  flex-shrink: 0;
  transition: transform 0.3s ease;
`;

const FAQAnswer = styled.div<{ $isOpen: boolean }>`
  max-height: ${(props) => (props.$isOpen ? '500px' : '0')};
  opacity: ${(props) => (props.$isOpen ? '1' : '0')};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const AnswerText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #555;
  margin: 0;
  padding: 0 28px 24px;
  line-height: 1.6;

  @media (max-width: 767px) {
    font-size: 14px;
    padding: 0 20px 20px;
  }
`;
